import React from 'react';
import styled, { css } from 'styled-components';
import { COLORS, sp } from '../styles';
import { InfoBorder } from './icons';
import Spinner from './Spinner';

export type NoticeLevel = 'info' | 'warning' | 'error';

const variants: Record<NoticeLevel, { bg: string; fg: string; border: string }> = {
    info: {
        bg: COLORS.noticeInfoBg,
        fg: COLORS.noticeInfoFg,
        border: COLORS.noticeInfoBorder,
    },
    warning: {
        bg: COLORS.noticeWarningBg,
        fg: COLORS.noticeWarningFg,
        border: COLORS.noticeWarningBorder,
    },
    error: {
        bg: COLORS.noticeErrorBg,
        fg: COLORS.noticeErrorFg,
        border: COLORS.noticeErrorBorder,
    },
};

const Wrapper = styled.div<{ level: NoticeLevel }>`
    display: flex;
    align-items: flex-start;
    gap: ${sp(2)};
    padding: ${sp(1.5)} ${sp(2)};
    border-radius: 6px;
    border: 1px solid;
    font-size: 13px;
    line-height: 1.4;
    ${({ level }) => {
        const v = variants[level];
        return css`
            background-color: ${v.bg};
            color: ${v.fg};
            border-color: ${v.border};
        `;
    }}
`;

const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    inline-size: 16px;
    block-size: 16px;
    flex-shrink: 0;

    svg {
        inline-size: 16px;
        block-size: 16px;
    }
`;

const Body = styled.div`
    flex-grow: 1;
`;

interface WarningNoticeProps {
    level: NoticeLevel;
    text: string;
    isLoading?: boolean;
    dataAuto?: string;
}

const WarningNotice: React.FC<WarningNoticeProps> = ({
    level,
    text,
    isLoading,
    dataAuto,
}) => (
    <Wrapper level={level} data-auto={dataAuto}>
        {isLoading ? (
            <IconSlot aria-hidden='true'>
                <Spinner />
            </IconSlot>
        ) : (
            <IconSlot aria-hidden='true'>
                <InfoBorder />
            </IconSlot>
        )}
        <Body>{text}</Body>
    </Wrapper>
);

export default WarningNotice;
